import { useQuery } from "@tanstack/react-query";
import {
  AccountDetails,
  AccountWithTicketsDetails,
  BillingInterval,
  InvoiceDetails,
  RemainingChargesDetails,
  SignupChargeDetails,
  SubscriptionGiftDetails,
  SubscriptionSource,
  UserFields,
} from "features/types";
import { get, patch, post, put } from "services/http";
import qs from "qs";
import { StripeTokenResponse } from "components/StripePaymentForm/StripePaymentForm";
import { SubscriptionChange } from "services/types";
import { Dayjs } from "dayjs";

export interface UpdateAccountRequest {
  seatPreference: "NONE" | "INCLUDE_ACCESSIBLE" | "ONLY_ACCESSIBLE";
  marketplace: boolean;
}

interface CreateUserRequest {
  email: string;
  firstName: string;
  lastName: string;
  userFields?: UserFields;
}

interface CreateSubscriptionGiftRequest {
  toUser: CreateUserRequest;
  planPackageId: number;
  quantity: number;
  deliverAt: string | Dayjs;
  source: SubscriptionSource;
}

interface UpdateGiftRequest {
  code: string;
  toUser: {
    firstName: string;
    lastName: string;
    email: string;
  };
  deliverAt: Dayjs;
}
interface AccountPrimaryUserRequest {
  userId: number;
  planPackageIds: number[];
}

export interface AccountMemberRequest {
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
  minor: string;
  userFields: UserFields;
  type: "PRIMARY" | "SECONDARY" | "ADDON" | "REMOVED";
  planPackageIds: number[];
}

interface ClaimSubscriptionGiftRequest {
  code: string;
  primaryUser: AccountPrimaryUserRequest;
  members?: AccountMemberRequest[];
  source: SubscriptionSource;
}

interface FetchSignupChargeRequest {
  billingInterval: BillingInterval;
  planPackageIds: number[];
  prorationDate?: string | Dayjs;
}

interface UpdateAccountMemberRequest {
  accountId: number;
  memberId: number;
  extUsername: string;
  userFields: UserFields;
}

export const ACCOUNT_QUERY_KEYS = {
  fetchAccountBff: "fetchAccountBFF",
};

export const useFetchAccountBFFQuery = () => {
  return useQuery(
    [ACCOUNT_QUERY_KEYS.fetchAccountBff],
    () =>
      get({
        url: "/bff/subscribers/account_with_tickets",
      }) as Promise<AccountWithTicketsDetails>,
    {
      staleTime: Infinity,
      enabled: false,
    }
  );
};

export const useFetchRemainingChargesQuery = ({
  accountId,
  planPackageId,
}: {
  accountId: number | undefined;
  planPackageId: number | string;
}) => {
  return useQuery(
    ["fetchRemainingCharges"],
    () =>
      get({
        url: `/accounts/${accountId}/remaining_charges/${planPackageId}`,
      }) as Promise<RemainingChargesDetails>
  );
};

export const fetchSignupChargeRequest = (params: FetchSignupChargeRequest) => {
  return get({
    url: `/accounts/signup_charge`,
    params,
    paramsSerializer: (params: FetchSignupChargeRequest) =>
      qs.stringify(params),
  }) as Promise<SignupChargeDetails>;
};

export const useFetchInvoices = () => {
  return useQuery(
    ["fetchInvoices"],
    () =>
      get({
        url: `/accounts/invoices`,
      }) as Promise<Array<InvoiceDetails>>
  );
};

export const linkMLBFetcher = ({
  accountId,
  memberId,
  extUsername,
}: {
  accountId: number;
  memberId: number;
  extUsername: string;
}) => {
  return patch({
    url: `accounts/${accountId}/members/${memberId}`,
    params: {
      extUsername,
    },
  }) as Promise<AccountDetails>;
};

export const addSubscriptionFetcher = ({
  billingInterval,
  userInfo,
  accountId,
  source,
}: {
  billingInterval: BillingInterval;
  userInfo: {
    userId: number;
    planPackageIds: number[];
  }[];
  accountId: number;
  source: SubscriptionSource;
}) => {
  return post({
    method: "post",
    url: `/accounts/${accountId}/subscriptions`,
    params: {
      billingInterval,
      subscriptions: userInfo,
      source,
    },
  }) as Promise<AccountDetails>;
};

export const addMembersFetcher = ({
  billingInterval,
  members,
  accountId,
  source,
}: {
  billingInterval: BillingInterval;
  members: {
    email: string;
    firstName: string;
    lastName: string;
    imageIdentifier: string;
    phone: string;
    address: string;
    addressLine2: string;
    city: string;
    state: string;
    country: string;
    postalCode: string;
    minor: boolean;
    userFields: UserFields;
    type: string;
    planPackageIds: Array<number>;
  };
  accountId: number;
  source: SubscriptionSource;
}) => {
  return post({
    url: `/accounts/${accountId}/members`,
    params: {
      billingInterval,
      members,
      source,
    },
    method: "POST",
  }) as Promise<AccountDetails>;
};

export const updateBillingFetcher = ({
  accountId,
  token,
}: {
  accountId: number;
  token: StripeTokenResponse;
}) => {
  const {
    token: { id },
    nameOnCard,
    postalCode,
  } = token;
  return patch({
    url: `/accounts/${accountId}/update_billing`,
    params: {
      billingToken: id,
      billingNameOnCard: nameOnCard,
      billingAddress: "",
      billingAddressLine2: "",
      billingCity: "",
      billingState: "",
      billingPostalCode: postalCode,
      billingCountry: "",
    },
  }) as Promise<AccountDetails>;
};

export const changeBillingIntervalFetcher = ({
  accountId,
  planPackageId,
  billingInterval = "FULL_SEASON",
}: {
  accountId: number;
  planPackageId: number;
  billingInterval?: BillingInterval;
}) => {
  return patch({
    url: `/accounts/${accountId}/change_billing_interval`,
    params: {
      billingInterval,
      planPackageId,
    },
  }) as Promise<AccountDetails>;
};

export const cancelOrReinstateSubscriptionFetcher = ({
  accountId,
  subscriptionId,
  type,
}: {
  accountId: number;
  subscriptionId: number;
  type: "cancel" | "reinstate";
}) => {
  return patch({
    url: `/accounts/${accountId}/subscriptions/${subscriptionId}/${type}`,
  }) as Promise<AccountDetails>;
};

export const getMemberBarcodeFetcher = (memberId: number) => {
  return get({
    url: `/accounts/members/${memberId}/barcode`,
  });
};

export const updateAccountFetcher = (params: UpdateAccountRequest) => {
  return patch({
    url: "/accounts",
    params,
  }) as Promise<AccountDetails>;
};

export const getPlanChangeChargeFetcher = ({
  accountId,
  changes,
  source,
}: {
  accountId: number;
  changes: SubscriptionChange[];
  source: SubscriptionSource;
}) => {
  return post({
    url: `/accounts/${accountId}/plan_change_charge`,
    params: {
      changes,
      source,
    },
  }) as Promise<SignupChargeDetails>;
};

export const changeSubscriptionsFetcher = ({
  accountId,
  changes,
  source,
}: {
  accountId: number;
  changes: SubscriptionChange[];
  source: SubscriptionSource;
}) => {
  return patch({
    url: `/accounts/${accountId}/subscriptions/change`,
    params: {
      changes,
      source,
    },
  }) as Promise<AccountDetails>;
};

export const purchaseGiftsRequest = (params: CreateSubscriptionGiftRequest) => {
  return post({
    url: "/accounts/gifts",
    params,
  }) as Promise<AccountDetails>;
};

export const useFetchGiftDetailsQuery = (code: string) => {
  return useQuery(
    ["fetchGift"],
    () =>
      get({
        url: `/accounts/gifts/${code}`,
      }) as Promise<SubscriptionGiftDetails>,
    {
      staleTime: Infinity,
    }
  );
};

export const updateGiftRequest = (params: UpdateGiftRequest) => {
  return put({
    url: `/accounts/gifts/${params.code}`,
    params,
  });
};

export const claimGiftRequest = (params: ClaimSubscriptionGiftRequest) => {
  return patch({
    url: `/accounts/gifts/${params.code}`,
    params,
  }) as Promise<AccountDetails>;
};

export const useFetchGiftHistoryQuery = () => {
  return useQuery(
    ["fetchGiftHistory"],
    () => {
      return get({
        url: "/accounts/gifts",
      }) as Promise<SubscriptionGiftDetails[]>;
    },
    {
      staleTime: Infinity,
    }
  );
};

export const updateAccountMemberRequest = ({
  accountId,
  memberId,
  extUsername,
  userFields,
}: UpdateAccountMemberRequest) =>
  patch({
    url: `/accounts/${accountId}/members/${memberId}`,
    params: {
      extUsername,
      userFields,
    },
  });
